import { NodeToolbar as NodeToolbarRaw, useStore } from '@xyflow/react';
import { Position } from '@xyflow/react';
import { Fragment, type ReactNode, useCallback, useEffect, useMemo, useRef, useState, isValidElement, cloneElement } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { TwoRowToolbar } from '@/components/nodes/shared/two-row-toolbar';
import { GenerateItem, DownloadItem } from '@/components/nodes/shared/items/actions-item';
import { HistoryItem } from '@/components/nodes/shared/items/history-item';
import { useProject } from '@/providers/project';
import { useUser } from '@/hooks/use-user';
import { useIsMultiSelect } from '@/components/canvas/utils/multi-select-store';
import {
  selectNodeLockedById,
  selectNodeSelectedById,
} from './store-selectors';

type NodeToolbarProps = {
  id: string;
  items:
    | {
        tooltip?: string;
        children: ReactNode;
      }[]
    | undefined;
};

export const NodeToolbar = ({ id, items }: NodeToolbarProps) => {
  const selectIsSelected = useMemo(() => selectNodeSelectedById(id), [id]);
  const selectIsLocked = useMemo(() => selectNodeLockedById(id), [id]);
  const isSelected = useStore(selectIsSelected) as boolean;
  const isLocked = useStore(selectIsLocked) as boolean;
  const isMultiSelect = useIsMultiSelect();
  const project = useProject();
  const { user } = useUser();
  const [isShown, setIsShown] = useState(false);
  const [pointerEnabled, setPointerEnabled] = useState(true);
  const pointerTimer = useRef<number | null>(null);
  const shouldShowRef = useRef(false);

  useEffect(() => {
    // Only show when exactly ONE node is selected (prevents multi-select flicker).
    const shouldShow = isSelected && !isMultiSelect;
    shouldShowRef.current = shouldShow;

    // 立即显示工具栏；短暂禁用指针事件，避免双击第二下命中工具栏
    if (shouldShow) {
      setIsShown(true);
      setPointerEnabled(false);
      if (pointerTimer.current) window.clearTimeout(pointerTimer.current);

      pointerTimer.current = window.setTimeout(() => {
        if (shouldShowRef.current) setPointerEnabled(true);
      }, 150);
    } else {
      setIsShown(false);
      setPointerEnabled(true);
      if (pointerTimer.current) window.clearTimeout(pointerTimer.current);
    }

    return () => {
      if (pointerTimer.current) window.clearTimeout(pointerTimer.current);
    };
  }, [isSelected, isMultiSelect]);

  const isOriginalOwner = useMemo(() => {
    try {
      const originOwnerId = (project as any)?.originOwnerUserId ?? (project as any)?.userId;
      return Boolean(user?.id) && String(user?.id) === String(originOwnerId ?? '');
    } catch {
      return false;
    }
  }, [project, user?.id]);

  const effectiveItems = useMemo(() => {
    if (!isLocked || isOriginalOwner || !Array.isArray(items)) return items;
    const kept: typeof items = [] as any;
    for (const it of items) {
      const child = (it as any)?.children as ReactNode;
      if (isValidElement(child) && child.type === TwoRowToolbar) {
        const controls = (((child as any).props?.controls) ?? []) as ReactNode[];
        const filtered = (Array.isArray(controls) ? controls : [controls]).filter((c) => {
          return isValidElement(c) && ((((c as any).type === GenerateItem) || ((c as any).type === DownloadItem) || ((c as any).type === HistoryItem)));
        });
        if (filtered.length > 0) {
          const cloned = cloneElement(child as any, { showInstructions: false, controls: filtered } as any);
          kept.push({ ...it, children: cloned });
        }
        continue;
      }
      if (isValidElement(child) && ((((child as any).type === GenerateItem) || ((child as any).type === DownloadItem) || ((child as any).type === HistoryItem)))) {
        kept.push(it);
      }
    }
    return kept;
  }, [isLocked, isOriginalOwner, items]);

  return (
    <NodeToolbarRaw
      isVisible={isShown}
      position={Position.Bottom}
      align="center"
      className={`flex items-center gap-0 rounded-none bg-transparent p-0 shadow-none transition-opacity ${
        pointerEnabled ? '' : 'pointer-events-none'
      }`}
    >
      {effectiveItems?.map((button, index) =>
        button.tooltip ? (
          <Tooltip key={button.tooltip}>
            <TooltipTrigger asChild>{button.children}</TooltipTrigger>
            <TooltipContent>{button.tooltip}</TooltipContent>
          </Tooltip>
        ) : (
          <Fragment key={index}>{button.children}</Fragment>
        )
      )}
    </NodeToolbarRaw>
  );
};
